import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import Sticky from 'react-stickynode';
import { DrawerProvider } from 'common/src/contexts/DrawerContext';
import { saasModernTheme } from 'common/src/theme/saasModern';
import { ResetCSS } from 'common/src/assets/css/style';
import { GlobalStyle, ContentWrapper } from '../containers/sassModern.style';

import BannerSection from '../containers/Banner';
import Navbar from '../containers/Navbar';
import WorkingProcessSection from '../containers/WorkingProcess';
import TextBoxSection from '../containers/TextBox';
import InfoSection from '../containers/Info';
import ComparisonTable from '../containers/ComparisonTable';
import Footer from '../containers/Footer';
import SEO from '../components/seo';
import FaqSection from '../containers/Faq';
import { useTranslation } from 'react-i18next';

const GoogleChromePasswordManager = ({ title, description, pageContext }) => {
  const { t } = useTranslation();

  const chromeFeatures = [
    {
      title: 'Chrome Toolbar Integration',
      alt: 'Chrome Toolbar Integration',
      description:
        "One-click access to your password vault directly from Chrome's toolbar. Instant autofill for any website without disrupting your browsing flow.",
    },
    {
      title: 'Chrome Autofill Enhancement',
      alt: 'Chrome Autofill Enhancement',
      description:
        "Seamlessly integrates with Chrome's native autofill capabilities while adding military-grade encryption. Works with all Chrome-compatible websites and web apps.",
    },
    {
      title: 'Chrome Web Store Available',
      alt: 'Chrome Web Store Available',
      description:
        'Available through the Chrome Web Store for easy installation. Regular updates ensure compatibility with the latest Chrome versions and security standards.',
    },
    {
      title: 'Multi-Device Chrome Sync',
      alt: 'Multi-Device Chrome Sync',
      description:
        'Use Chrome on desktop, laptop, and tablet with instant password synchronization. Your Chrome passwords follow you across Windows, Mac, Linux, and ChromeOS.',
    },
    {
      title: 'Chrome Password Generator',
      alt: 'Chrome Password Generator',
      description:
        'Generate strong, unique passwords directly within Chrome as you create new accounts. Customizable password length and complexity options built into the extension.',
    },
    {
      title: 'Chrome Security Reports',
      alt: 'Chrome Security Reports',
      description:
        'Run password health checks directly from your Chrome browser. Identify weak passwords, reused credentials, and compromised accounts with one click.',
    },
  ];

  const chromeFaqData = [
    {
      title: 'Is Psono a reliable password manager for Chrome?',
      description:
        'Psono delivers professional-grade password management for Chrome users at no cost. The extension is built on open-source foundations, allowing security professionals to verify its implementation. Organizations can self-host their instances for complete data sovereignty. Psono combines strong NaCl encryption with cross-device synchronization and generous free-tier features, making it a compelling choice for Chrome users seeking alternatives to paid solutions.',
      expend: false,
    },
    {
      title: 'How do I set up Psono in Chrome?',
      description:
        "Start by creating your Psono account at psono.pw and verifying your email. Launch Chrome and navigate to the Chrome Web Store where you'll find the Psono extension. After installation, locate the Psono icon in your toolbar and authenticate with your account. Once signed in, Psono activates automatically, capturing and securing credentials as you browse while maintaining sync across all connected devices.",
      expend: false,
    },
    {
      title: 'Which free password manager works best with Chrome?',
      description:
        'For Chrome users seeking no-cost password management, Psono offers compelling advantages. Built on transparent open-source code that security experts can audit, Psono provides enterprise-level features without subscription fees. After quick installation from the Chrome Web Store, the extension operates seamlessly in the background—capturing credentials, generating strong passwords, and synchronizing your vault across all devices automatically.',
      expend: false,
    },
    {
      title: 'Can I export my passwords from Psono?',
      description:
        'Psono provides full data portability through its export feature. Access the web application, navigate to the "Other" menu, and select "Export". Choose your datastore and select either JSON or CSV format based on your needs. Confirm your selection to download a file containing all vault items accessible to your account. This ensures you always maintain control over your password data.',
      expend: false,
    },
    {
      title: 'How do I migrate passwords from Chrome to Psono?',
      description:
        'Begin by exporting credentials from Chrome: Open Settings > Autofill and passwords > Password Manager, select the menu (three dots), and choose "Export passwords" to save a CSV file. In Psono\'s web interface, access "Other" > "Import", select "Chrome CSV" as your source format, upload the exported file, and confirm the import. Your Chrome-stored passwords will transfer into your Psono vault, ready for encrypted storage and cross-device sync.',
      expend: false,
    },
  ];

  const comparisonFeatures = [
    {
      title: 'Advanced Encryption',
      description:
        'Protect passwords with NaCl cryptography. Vault data is encrypted client-side before it is sent to the server.',
      psonoValue: true,
      chromeValue: '-',
    },
    {
      title: 'Open Source & Auditable',
      description:
        'Publicly available source code allows security experts worldwide to verify security claims. Regular third-party audits ensure continued protection.',
      psonoValue: true,
      chromeValue: '-',
    },
    {
      title: 'Advanced Two-Factor Authentication',
      description:
        'Secure your vault with TOTP authenticators, hardware security keys, or FIDO2 WebAuthn credentials for enhanced protection.',
      psonoValue: true,
      chromeValue: '-',
    },
    {
      title: 'Password Generator',
      description:
        'Create long, complex, and unique passwords for every site with customizable options for maximum security.',
      psonoValue: true,
      chromeValue: '-',
    },
    {
      title: 'Self-Hosting Option',
      description:
        'Deploy on your own infrastructure for complete control over your data and compliance with regulatory requirements.',
      psonoValue: true,
      chromeValue: '-',
    },
    {
      title: 'Cross-Platform Sync',
      description:
        'Access your passwords on any browser, mobile device, or desktop OS with automatic synchronization across all devices.',
      psonoValue: true,
      chromeValue: 'Limited',
    },
  ];

  return (
    <ThemeProvider theme={saasModernTheme}>
      <Fragment>
        <SEO
          title={t('Best Free Password Manager Google Chrome | Psono')}
          lang={pageContext.lang}
          titleTemplate={`%s`}
          description={t(
            'Secure password manager extension for Chrome. Store unlimited passwords, sync across devices, and protect your data with military-grade encryption. Free open-source solution.'
          )}
          keywords={[
            'password manager chrome',
            'chrome password extension',
            'password manager google',
            'best free password manager for chrome',
            'google chrome password manager',
            'chrome extension password manager',
            'secure password manager chrome',
            'chrome password vault',
            'browser password manager chrome',
          ]}
        />

        <ResetCSS />
        <GlobalStyle />

        <ContentWrapper>
          <Sticky top={0} innerZ={9999} activeClass="sticky-nav-active">
            <DrawerProvider>
              <Navbar pageContext={pageContext} />
            </DrawerProvider>
          </Sticky>
          <BannerSection
            pageContext={pageContext}
            topText={t('Open-source password security for Chrome')}
            titleText={t('Google Chrome Password Manager')}
            descriptionText={t(
              'The best free password manager for Chrome. Secure Chrome password extension with unlimited storage, autofill, and military-grade encryption across all devices.'
            )}
            primeLink={
              'https://chromewebstore.google.com/detail/psono-free-password-manag/eljmjmgjkbmpmfljlmklcfineebidmlo?hl=en'
            }
            primeText={t('INSTALL PSONO FOR CHROME NOW')}
            downloadLink={`${pageContext.langPathPrefix}/download`}
            downloadText={t('VIEW ALL DOWNLOAD OPTIONS')}
          />
          <TextBoxSection
            description={t(
              'Trusted by users worldwide for Chrome password management'
            )}
            text={t(
              'Psono brings enterprise-level security to Chrome users through an open-source password manager you can trust. Whether managing personal accounts or business credentials, enjoy unlimited storage, automatic sync across all platforms, and lifetime free access to essential features.'
            )}
          />
          <ComparisonTable
            features={comparisonFeatures}
            browserName="Chrome"
            title={t(
              'Top reasons to choose Psono over Google Chrome native password manager'
            )}
          />
          <TextBoxSection
            description={t(
              'Why Chrome users need a dedicated password manager'
            )}
            text={t(
              "Built-in browser password storage handles basic credential saving, but falls short on comprehensive security. The primary threats come from password reuse, weak credentials, and lack of advanced security features. Psono addresses these vulnerabilities by creating cryptographically unique passwords for each account, implementing client-side NaCl encryption before vault data is sent to the server, and providing multi-factor authentication for vault access. Integration with Chrome's toolbar ensures convenient access while maintaining security throughout storage and cross-device synchronization."
            )}
          />
          <WorkingProcessSection
            title={t('Why choose Psono as your Chrome password manager?')}
            description={t(
              'Key features that make Psono stand out for Chrome users'
            )}
            buttonUrl="/features-for-users"
            items={chromeFeatures}
            pageContext={pageContext}
          />
          <TextBoxSection
            description={t('Getting started with Psono on Chrome')}
            text={t(
              "Installing Psono takes under five minutes: Begin by registering for your Psono account and confirming your email. Head to the Chrome Web Store to install the extension—once added, you'll find the Psono icon positioned in your browser toolbar. Sign in through the extension using your account details. Psono immediately starts working, prompting you to save credentials whenever you log into websites. Future logins become effortless—just click the toolbar icon to instantly fill your username and password. Your vault synchronizes in real-time across every platform you use, from Windows and Mac to iOS and Android devices."
            )}
          />
          <InfoSection
            pageContext={pageContext}
            titleContent={t('Chrome Extension Security')}
            descriptionContent={t(
              "Psono's Chrome extension employs NaCl cryptographic libraries for client-side encryption, protecting your credentials before transmission. The fully transparent, open-source codebase undergoes continuous review by the global security community. From individual users securing personal logins to organizations protecting corporate access credentials, Psono provides battle-tested security architecture integrated seamlessly into your Chrome workflow."
            )}
          />
          <FaqSection data={chromeFaqData} />
          <Footer pageContext={pageContext} />
        </ContentWrapper>
      </Fragment>
    </ThemeProvider>
  );
};

GoogleChromePasswordManager.propTypes = {
  title: PropTypes.object,
  description: PropTypes.object,
};

GoogleChromePasswordManager.defaultProps = {
  title: {
    fontSize: ['24px', '32px', '40px', '42px', '46px'],
    fontWeight: '700',
    color: '#fff',
    letterSpacing: '-0.025em',
    mb: ['20px', '25px', '25px', '25px', '25px'],
    lineHeight: '1.2',
    textAlign: 'center',
  },
  description: {
    fontSize: ['15px', '16px', '16px', '16px', '16px'],
    color: '#fff',
    lineHeight: '1.75',
    mb: '0',
    textAlign: 'center',
  },
};

export default GoogleChromePasswordManager;
